import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    ArrowLeft,
    Plus,
    Save,
    Trash2,
    GripVertical,
    Dumbbell,
    Clock,
    ChevronDown,
    ChevronUp,
    X,
    Loader2,
    Search,
    Check,
    Copy,
    Zap,
    Target,
    Flame,
} from 'lucide-react';

interface Exercise {
    id: string;
    name: string;
    category: string;
    muscle_groups: string[];
    thumbnail_url: string | null;
    video_url: string | null;
}

interface BlockExercise {
    id?: string;
    exercise_id: string;
    exercise?: Exercise;
    order_index: number;
    sets: number;
    reps: string;
    weight_kg: number | null;
    rest_seconds: number;
    tempo: string | null;
    notes: string | null;
}

interface WorkoutBlock {
    id?: string;
    name: string;
    block_type: string;
    order_index: number;
    notes: string | null;
    exercises: BlockExercise[];
    isExpanded?: boolean;
}

interface WorkoutData {
    id?: string;
    name: string;
    description: string;
    duration_minutes: number | null;
    difficulty: string;
    focus_areas: string[];
    is_template: boolean;
    blocks: WorkoutBlock[];
}

const BLOCK_TYPES = [
    { value: 'warmup', label: 'Aquecimento', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'main', label: 'Principal', color: 'bg-primary-100 text-primary-700' },
    { value: 'superset', label: 'Superset', color: 'bg-purple-100 text-purple-700' },
    { value: 'circuit', label: 'Circuito', color: 'bg-blue-100 text-blue-700' },
    { value: 'cooldown', label: 'Volta à Calma', color: 'bg-green-100 text-green-700' },
];

const DIFFICULTIES = ['Iniciante', 'Intermediário', 'Avançado'];

const FOCUS_AREAS = [
    'Peito', 'Costas', 'Ombros', 'Braços', 'Core', 'Pernas', 'Glúteos', 'Corpo Todo', 'Cardio'
];

// Quick add templates
const QUICK_TEMPLATES = [
    {
        name: 'Treino A - Superior',
        icon: '💪',
        difficulty: 'Intermediário',
        focus_areas: ['Peito', 'Ombros', 'Braços'],
        blocks: [
            { name: 'Aquecimento', type: 'warmup', exercises: ['Polichinelo', 'Rotação de ombros'] },
            { name: 'Peito', type: 'main', exercises: ['Supino reto', 'Supino inclinado', 'Crossover'] },
            { name: 'Ombros', type: 'main', exercises: ['Desenvolvimento', 'Elevação lateral'] },
            { name: 'Tríceps', type: 'main', exercises: ['Tríceps corda', 'Tríceps francês'] },
        ]
    },
    {
        name: 'Treino B - Inferior',
        icon: '🦵',
        difficulty: 'Intermediário',
        focus_areas: ['Pernas', 'Glúteos'],
        blocks: [
            { name: 'Aquecimento', type: 'warmup', exercises: ['Bicicleta', 'Agachamento livre'] },
            { name: 'Quadríceps', type: 'main', exercises: ['Leg Press', 'Cadeira extensora', 'Agachamento'] },
            { name: 'Posterior', type: 'main', exercises: ['Stiff', 'Cadeira flexora'] },
            { name: 'Glúteos', type: 'main', exercises: ['Hip Thrust', 'Abdução'] },
        ]
    },
    {
        name: 'Full Body Express',
        icon: '⚡',
        difficulty: 'Iniciante',
        focus_areas: ['Corpo Todo'],
        blocks: [
            { name: 'Circuito', type: 'circuit', exercises: ['Agachamento', 'Flexão', 'Remada', 'Prancha'] },
        ]
    },
];

export function WorkoutBuilderPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { tenant } = useAuth();
    const queryClient = useQueryClient();
    const isNew = id === 'new';

    const [workout, setWorkout] = useState<WorkoutData>({
        name: '',
        description: '',
        duration_minutes: 60,
        difficulty: 'Intermediário',
        focus_areas: [],
        is_template: false,
        blocks: [],
    });

    const [showExercisePicker, setShowExercisePicker] = useState<{
        blockIndex: number;
    } | null>(null);
    const [exerciseSearch, setExerciseSearch] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Fetch existing workout
    const { data: existingWorkout, isLoading: isLoadingWorkout } = useQuery({
        queryKey: ['workout', id],
        queryFn: async () => {
            if (isNew) return null;

            const { data, error } = await supabase
                .from('workouts')
                .select(`
                    *,
                    workout_blocks (
                        *,
                        block_exercises (
                            *,
                            exercise:exercise_id (
                                id, name, category, muscle_groups, thumbnail_url, video_url
                            )
                        )
                    )
                `)
                .eq('id', id)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: !isNew && !!id,
    });

    // Fetch exercises for picker
    const { data: exercises } = useQuery({
        queryKey: ['exercises', tenant?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('exercises')
                .select('id, name, category, muscle_groups, thumbnail_url, video_url')
                .or(`is_global.eq.true,tenant_id.eq.${tenant?.id}`)
                .eq('is_active', true)
                .order('name');

            if (error) throw error;
            return data as Exercise[];
        },
        enabled: !!tenant?.id,
    });

    // Load existing workout data
    useEffect(() => {
        if (existingWorkout) {
            setWorkout({
                id: existingWorkout.id,
                name: existingWorkout.name,
                description: existingWorkout.description || '',
                duration_minutes: existingWorkout.duration_minutes,
                difficulty: existingWorkout.difficulty,
                focus_areas: existingWorkout.focus_areas || [],
                is_template: existingWorkout.is_template,
                blocks: (existingWorkout.workout_blocks || [])
                    .sort((a: WorkoutBlock, b: WorkoutBlock) => a.order_index - b.order_index)
                    .map((block: WorkoutBlock & { block_exercises: BlockExercise[] }) => ({
                        id: block.id,
                        name: block.name,
                        block_type: block.block_type,
                        order_index: block.order_index,
                        notes: block.notes,
                        isExpanded: true,
                        exercises: (block.block_exercises || [])
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((ex) => ({
                                id: ex.id,
                                exercise_id: ex.exercise_id,
                                exercise: ex.exercise as Exercise,
                                order_index: ex.order_index,
                                sets: ex.sets,
                                reps: ex.reps,
                                weight_kg: ex.weight_kg,
                                rest_seconds: ex.rest_seconds,
                                tempo: ex.tempo,
                                notes: ex.notes,
                            })),
                    })),
            });
        }
    }, [existingWorkout]);

    // Save workout
    const handleSave = async () => {
        if (!tenant?.id || !workout.name) return;

        setIsSaving(true);

        try {
            let workoutId = workout.id;

            if (isNew) {
                // Create workout
                const { data: newWorkout, error } = await supabase
                    .from('workouts')
                    .insert({
                        tenant_id: tenant.id,
                        name: workout.name,
                        description: workout.description || null,
                        duration_minutes: workout.duration_minutes,
                        difficulty: workout.difficulty,
                        focus_areas: workout.focus_areas,
                        is_template: workout.is_template,
                        is_active: true,
                    })
                    .select()
                    .single();

                if (error) throw error;
                workoutId = newWorkout.id;
            } else {
                // Update workout
                const { error } = await supabase
                    .from('workouts')
                    .update({
                        name: workout.name,
                        description: workout.description || null,
                        duration_minutes: workout.duration_minutes,
                        difficulty: workout.difficulty,
                        focus_areas: workout.focus_areas,
                        is_template: workout.is_template,
                    })
                    .eq('id', workoutId);

                if (error) throw error;

                // Delete existing blocks (cascade deletes exercises)
                await supabase.from('workout_blocks').delete().eq('workout_id', workoutId);
            }

            // Create blocks and exercises
            for (const [blockIndex, block] of workout.blocks.entries()) {
                const { data: newBlock, error: blockError } = await supabase
                    .from('workout_blocks')
                    .insert({
                        workout_id: workoutId,
                        name: block.name,
                        block_type: block.block_type,
                        order_index: blockIndex,
                        notes: block.notes,
                    })
                    .select()
                    .single();

                if (blockError) throw blockError;

                // Create exercises in block
                for (const [exIndex, exercise] of block.exercises.entries()) {
                    const { error: exError } = await supabase.from('block_exercises').insert({
                        block_id: newBlock.id,
                        exercise_id: exercise.exercise_id,
                        order_index: exIndex,
                        sets: exercise.sets,
                        reps: exercise.reps,
                        weight_kg: exercise.weight_kg,
                        rest_seconds: exercise.rest_seconds,
                        tempo: exercise.tempo,
                        notes: exercise.notes,
                    });

                    if (exError) throw exError;
                }
            }

            queryClient.invalidateQueries({ queryKey: ['workouts'] });
            navigate('/workouts');
        } catch (error) {
            console.error('Error saving workout:', error);
            alert('Erro ao salvar treino. Tente novamente.');
        } finally {
            setIsSaving(false);
        }
    };

    // Block management
    const addBlock = (type: string) => {
        const newBlock: WorkoutBlock = {
            name: BLOCK_TYPES.find((t) => t.value === type)?.label || 'Novo Bloco',
            block_type: type,
            order_index: workout.blocks.length,
            notes: null,
            exercises: [],
            isExpanded: true,
        };
        setWorkout({ ...workout, blocks: [...workout.blocks, newBlock] });
    };

    const removeBlock = (index: number) => {
        const newBlocks = [...workout.blocks];
        newBlocks.splice(index, 1);
        setWorkout({ ...workout, blocks: newBlocks });
    };

    const toggleBlockExpand = (index: number) => {
        const newBlocks = [...workout.blocks];
        newBlocks[index] = { ...newBlocks[index], isExpanded: !newBlocks[index].isExpanded };
        setWorkout({ ...workout, blocks: newBlocks });
    };

    const updateBlock = (index: number, updates: Partial<WorkoutBlock>) => {
        const newBlocks = [...workout.blocks];
        newBlocks[index] = { ...newBlocks[index], ...updates };
        setWorkout({ ...workout, blocks: newBlocks });
    };

    // Exercise management
    const addExercise = (blockIndex: number, exercise: Exercise) => {
        const newBlocks = [...workout.blocks];
        const newExercise: BlockExercise = {
            exercise_id: exercise.id,
            exercise,
            order_index: newBlocks[blockIndex].exercises.length,
            sets: 3,
            reps: '12',
            weight_kg: null,
            rest_seconds: 60,
            tempo: null,
            notes: null,
        };
        newBlocks[blockIndex].exercises.push(newExercise);
        setWorkout({ ...workout, blocks: newBlocks });
        setShowExercisePicker(null);
    };

    const updateExercise = (
        blockIndex: number,
        exerciseIndex: number,
        updates: Partial<BlockExercise>
    ) => {
        const newBlocks = [...workout.blocks];
        newBlocks[blockIndex].exercises[exerciseIndex] = {
            ...newBlocks[blockIndex].exercises[exerciseIndex],
            ...updates,
        };
        setWorkout({ ...workout, blocks: newBlocks });
    };

    const removeExercise = (blockIndex: number, exerciseIndex: number) => {
        const newBlocks = [...workout.blocks];
        newBlocks[blockIndex].exercises.splice(exerciseIndex, 1);
        setWorkout({ ...workout, blocks: newBlocks });
    };

    // Filtered exercises for picker
    const filteredExercises = exercises?.filter((ex) =>
        ex.name.toLowerCase().includes(exerciseSearch.toLowerCase()) ||
        ex.category.toLowerCase().includes(exerciseSearch.toLowerCase())
    );

    if (!isNew && isLoadingWorkout) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
                    <p className="text-gray-500">Carregando treino...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/workouts')}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="section-title">
                            {isNew ? 'Novo Treino' : 'Editar Treino'}
                        </h1>
                        <p className="section-subtitle">Monte o treino perfeito</p>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={isSaving || !workout.name}
                    className="btn-primary"
                >
                    {isSaving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                        <Save className="w-4 h-4 mr-2" />
                    )}
                    Salvar Treino
                </button>
            </div>

            {/* Workout Info */}
            <div className="card">
                <h2 className="font-semibold mb-4">Informações do Treino</h2>
                <div className="grid gap-4 md:grid-cols-2">
                    <div>
                        <label className="label">Nome do Treino *</label>
                        <input
                            type="text"
                            value={workout.name}
                            onChange={(e) => setWorkout({ ...workout, name: e.target.value })}
                            placeholder="Ex: Treino A - Peito e Tríceps"
                            className="input"
                        />
                    </div>
                    <div>
                        <label className="label">Duração (minutos)</label>
                        <input
                            type="number"
                            value={workout.duration_minutes || ''}
                            onChange={(e) =>
                                setWorkout({
                                    ...workout,
                                    duration_minutes: e.target.value ? parseInt(e.target.value) : null,
                                })
                            }
                            placeholder="60"
                            className="input"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="label">Descrição</label>
                        <textarea
                            value={workout.description}
                            onChange={(e) => setWorkout({ ...workout, description: e.target.value })}
                            placeholder="Breve descrição do treino..."
                            className="input"
                            rows={2}
                        />
                    </div>
                    <div>
                        <label className="label">Dificuldade</label>
                        <div className="flex gap-2">
                            {DIFFICULTIES.map((d) => (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setWorkout({ ...workout, difficulty: d })}
                                    className={`btn flex-1 ${workout.difficulty === d ? 'btn-primary' : 'btn-secondary'
                                        }`}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="label">Foco</label>
                        <div className="flex flex-wrap gap-2">
                            {FOCUS_AREAS.map((area) => (
                                <button
                                    key={area}
                                    type="button"
                                    onClick={() => {
                                        const newAreas = workout.focus_areas.includes(area)
                                            ? workout.focus_areas.filter((a) => a !== area)
                                            : [...workout.focus_areas, area];
                                        setWorkout({ ...workout, focus_areas: newAreas });
                                    }}
                                    className={`badge ${workout.focus_areas.includes(area)
                                        ? 'badge-primary'
                                        : 'badge-secondary'
                                        }`}
                                >
                                    {area}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={workout.is_template}
                                onChange={(e) =>
                                    setWorkout({ ...workout, is_template: e.target.checked })
                                }
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Salvar como template (reutilizável)
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            {/* Blocks */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">Blocos de Exercícios</h2>
                    <div className="flex gap-2">
                        {BLOCK_TYPES.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => addBlock(type.value)}
                                className="btn-ghost btn-sm"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                {type.label}
                            </button>
                        ))}
                    </div>
                </div>

                {workout.blocks.length === 0 ? (
                    <div className="space-y-6">
                        {/* Quick Templates */}
                        <div className="card bg-gradient-to-br from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 border-primary-200 dark:border-primary-800">
                            <div className="flex items-center gap-2 mb-4">
                                <Zap className="w-5 h-5 text-primary-600" />
                                <h3 className="font-semibold text-gray-900 dark:text-white">Templates Rápidos</h3>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-3">
                                {QUICK_TEMPLATES.map((template, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            setWorkout({
                                                ...workout,
                                                name: template.name,
                                                difficulty: template.difficulty,
                                                focus_areas: template.focus_areas,
                                                blocks: template.blocks.map((b, idx) => ({
                                                    name: b.name,
                                                    block_type: b.type,
                                                    order_index: idx,
                                                    notes: null,
                                                    exercises: [],
                                                    isExpanded: true,
                                                })),
                                            });
                                        }}
                                        className="p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-600 hover:shadow-lg transition-all text-left group"
                                    >
                                        <span className="text-2xl mb-2 block">{template.icon}</span>
                                        <p className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600">
                                            {template.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {template.blocks.length} blocos
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Empty state */}
                        <div className="card text-center py-12 border-dashed border-2">
                            <Dumbbell className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4 animate-float" />
                            <h3 className="font-medium text-gray-900 dark:text-white mb-2">Monte do zero</h3>
                            <p className="text-gray-500 mb-4">
                                Adicione blocos para organizar os exercícios
                            </p>
                            <button onClick={() => addBlock('main')} className="btn-primary">
                                <Plus className="w-4 h-4 mr-2" />
                                Adicionar Bloco Principal
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {workout.blocks.map((block, blockIndex) => {
                            const blockType = BLOCK_TYPES.find((t) => t.value === block.block_type);

                            return (
                                <div key={blockIndex} className="card border-l-4 border-l-primary-500">
                                    {/* Block Header */}
                                    <div
                                        className="flex items-center gap-3 cursor-pointer"
                                        onClick={() => toggleBlockExpand(blockIndex)}
                                    >
                                        <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                                        <span className={`badge ${blockType?.color || 'badge-secondary'}`}>
                                            {blockType?.label || block.block_type}
                                        </span>
                                        <input
                                            type="text"
                                            value={block.name}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                updateBlock(blockIndex, { name: e.target.value });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex-1 font-medium bg-transparent border-none focus:outline-none focus:ring-0"
                                        />
                                        <span className="text-sm text-gray-500">
                                            {block.exercises.length} exercícios
                                        </span>
                                        {block.isExpanded ? (
                                            <ChevronUp className="w-4 h-4 text-gray-400" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-400" />
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeBlock(blockIndex);
                                            }}
                                            className="p-1 hover:bg-red-50 rounded text-red-600"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Block Content */}
                                    {block.isExpanded && (
                                        <div className="mt-4 space-y-3">
                                            {block.exercises.map((exercise, exIndex) => (
                                                <ExerciseRow
                                                    key={exIndex}
                                                    exercise={exercise}
                                                    onUpdate={(updates) =>
                                                        updateExercise(blockIndex, exIndex, updates)
                                                    }
                                                    onRemove={() => removeExercise(blockIndex, exIndex)}
                                                />
                                            ))}

                                            <button
                                                onClick={() =>
                                                    setShowExercisePicker({ blockIndex })
                                                }
                                                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Plus className="w-4 h-4" />
                                                Adicionar Exercício
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Exercise Picker Modal */}
            {showExercisePicker && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden animate-scale-in">
                        <div className="p-4 border-b">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold">Selecionar Exercício</h3>
                                <button
                                    onClick={() => setShowExercisePicker(null)}
                                    className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={exerciseSearch}
                                    onChange={(e) => setExerciseSearch(e.target.value)}
                                    placeholder="Buscar exercício..."
                                    className="input pl-9"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto max-h-[60vh]">
                            {filteredExercises?.map((exercise) => (
                                <button
                                    key={exercise.id}
                                    onClick={() =>
                                        addExercise(showExercisePicker.blockIndex, exercise)
                                    }
                                    className="w-full p-4 flex items-center gap-3 hover:bg-gray-50 border-b text-left"
                                >
                                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                                        {exercise.video_url ? (
                                            <img
                                                src={`https://img.youtube.com/vi/${exercise.video_url.match(
                                                    /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/
                                                )?.[1]}/default.jpg`}
                                                alt=""
                                                className="w-full h-full object-cover rounded-lg"
                                            />
                                        ) : (
                                            <Dumbbell className="w-5 h-5 text-gray-400" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-gray-900">{exercise.name}</p>
                                        <p className="text-sm text-gray-500">{exercise.category}</p>
                                    </div>
                                    <Check className="w-5 h-5 text-primary-600 opacity-0 group-hover:opacity-100" />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function ExerciseRow({
    exercise,
    onUpdate,
    onRemove,
}: {
    exercise: BlockExercise;
    onUpdate: (updates: Partial<BlockExercise>) => void;
    onRemove: () => void;
}) {
    return (
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl group transition-all hover:shadow-md hover:bg-white dark:hover:bg-gray-800 animate-slide-up">
            <GripVertical className="w-4 h-4 text-gray-400 cursor-grab hover:text-primary-600" />

            {/* Exercise info */}
            <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                    {exercise.exercise?.name || 'Exercício'}
                </p>
                <p className="text-xs text-gray-500">{exercise.exercise?.category}</p>
            </div>

            {/* Sets */}
            <div className="w-16">
                <label className="text-xs text-gray-500">Séries</label>
                <input
                    type="number"
                    value={exercise.sets}
                    onChange={(e) => onUpdate({ sets: parseInt(e.target.value) || 0 })}
                    className="input text-center text-sm py-1"
                    min="1"
                />
            </div>

            {/* Reps */}
            <div className="w-20">
                <label className="text-xs text-gray-500">Reps</label>
                <input
                    type="text"
                    value={exercise.reps}
                    onChange={(e) => onUpdate({ reps: e.target.value })}
                    placeholder="12"
                    className="input text-center text-sm py-1"
                />
            </div>

            {/* Rest */}
            <div className="w-20">
                <label className="text-xs text-gray-500">Descanso</label>
                <div className="flex items-center gap-1">
                    <input
                        type="number"
                        value={exercise.rest_seconds}
                        onChange={(e) => onUpdate({ rest_seconds: parseInt(e.target.value) || 0 })}
                        className="input text-center text-sm py-1"
                        min="0"
                    />
                    <span className="text-xs text-gray-500">s</span>
                </div>
            </div>

            {/* Remove */}
            <button
                onClick={onRemove}
                className="p-1 hover:bg-red-50 rounded text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
